import React from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  TextField,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NotesIcon from '@mui/icons-material/Notes';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import LinkIcon from '@mui/icons-material/Link';
import PhoneIcon from '@mui/icons-material/Phone';
import TagIcon from '@mui/icons-material/Tag';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PercentIcon from '@mui/icons-material/Percent';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PinIcon from '@mui/icons-material/Pin';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ChecklistIcon from '@mui/icons-material/Checklist';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import useDatabasesStore from './useDatabasesStore';

export const COLUMN_TYPES = [
  // Text
  { value: 'TEXT', label: 'Text', icon: TextFieldsIcon },
  { value: 'RICH_TEXT', label: 'Rich Text', icon: NotesIcon },
  { value: 'EMAIL', label: 'Email', icon: AlternateEmailIcon },
  { value: 'URL', label: 'URL', icon: LinkIcon },
  { value: 'PHONE', label: 'Phone', icon: PhoneIcon },
  // Numeric
  { value: 'NUMBER', label: 'Number', icon: TagIcon },
  { value: 'CURRENCY', label: 'Currency', icon: AttachMoneyIcon },
  { value: 'PERCENT', label: 'Percent', icon: PercentIcon },
  { value: 'RATING', label: 'Rating', icon: StarOutlineIcon },
  { value: 'DURATION', label: 'Duration', icon: TimerOutlinedIcon },
  { value: 'AUTONUMBER', label: 'Auto Number', icon: PinIcon },
  // Boolean
  { value: 'CHECKBOX', label: 'Checkbox', icon: CheckBoxOutlinedIcon },
  // Date/Time
  { value: 'DATETIME', label: 'Date & Time', icon: CalendarTodayIcon },
  // Selection
  { value: 'SINGLE_SELECT', label: 'Single Select', icon: RadioButtonCheckedIcon },
  { value: 'MULTIPLE_SELECT', label: 'Multiple Select', icon: ChecklistIcon },
  // Relational
  { value: 'LINKED_RECORD', label: 'Linked Record', icon: InsertLinkIcon },
  // AI
  { value: 'AI_AGENT', label: 'AI Agent', icon: SmartToyOutlinedIcon },
];

function DatabaseColumnItem({ item, index, fields, workflow = false, setColumnWorkflow }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          variant="outlined"
          placeholder="Column name"
          fullWidth
          size="small"
          defaultValue={item.columnName}
          onBlur={event => {
            if (workflow) {
              setColumnWorkflow(event);
            } else {
              useDatabasesStore.getState().setField(event.target.value, index, 'columnName');
            }
          }}
          error={!!item.errorMessage}
          InputProps={{
            endAdornment: item.errorMessage && (
              <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 18 }} />
            ),
          }}
          helperText={item.errorMessage || null}
        />
      </Box>

      <Select
        size="small"
        variant="outlined"
        value={item.columnType}
        onChange={event => {
          useDatabasesStore.getState().setField(event.target.value, index, 'columnType');
        }}
        disabled={item.workflow}
        sx={{
          minWidth: 160,
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.75,
          },
        }}
        renderValue={(selected) => {
          const type = COLUMN_TYPES.find(t => t.value === selected);
          if (!type) return selected;
          const Icon = type.icon;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />
              {type.label}
            </Box>
          );
        }}
      >
        {COLUMN_TYPES.map(type => {
          const Icon = type.icon;
          return (
            <MenuItem key={type.value} value={type.value}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Icon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </ListItemIcon>
              <ListItemText primary={type.label} />
            </MenuItem>
          );
        })}
      </Select>

      {fields && fields.length > 1 && (
        <IconButton
          size="small"
          onClick={() => useDatabasesStore.getState().deleteComponentKeyData(item)}
          sx={{ mt: 0.5, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}
    </Box>
  );
}

export default DatabaseColumnItem;
